import React from 'react';
import i18n from '../../../../../../i18n';
import { drillRankColor } from './colors';

export type CascadeRow = {
  name: string;
  value: number;
  color?: string;
  /** Compare-period value for this row. */
  compareValue?: number;
  /** Signed percent-change of value vs compareValue. */
  vsComparePct?: number | null;
};

/** Dense ordered share cascade — labels + thick bars + share/value. */
export function DrillShareCascade({
  rows,
  fmtVal,
  maxRows = 8,
  compareLabel,
}: {
  rows: CascadeRow[];
  fmtVal: (v: number) => string;
  maxRows?: number;
  /** Legend label for the compare chip, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
}) {
  const data = rows.filter(r => r.value > 0).slice(0, maxRows);
  if (!data.length) return null;
  const total = data.reduce((a, r) => a + r.value, 0) || 1;
  const peak = Math.max(...data.map(r => r.value), 0.0001);
  const hasCompare = data.some(r => r.compareValue != null);

  return (
    <div className="ddrill-cascade" onClick={e => e.stopPropagation()}>
      {data.map((r, i) => {
        const share = Math.round((r.value / total) * 100);
        const width = Math.max(4, (r.value / peak) * 100);
        return (
          <div className="ddrill-cascade-row" key={r.name}>
            <span className="ddrill-cascade-name" title={r.name}>
              {r.name}
            </span>
            <span className="ddrill-cascade-track" aria-hidden>
              <span
                className="ddrill-cascade-fill"
                style={{
                  width: `${width}%`,
                  background: r.color ?? drillRankColor(i, data.length),
                }}
              />
            </span>
            <span className="ddrill-cascade-meta">
              <b>{share}%</b>
              <span>{fmtVal(r.value)}</span>
              {hasCompare && r.compareValue != null ? (
                <span
                  className="dcrux-cmp-chip"
                  title={compareLabel ? `${compareLabel}: ${fmtVal(r.compareValue)}` : i18n.t('performance:drillUi.vsCompare')}
                >
                  {i18n.t('performance:drillUi.wasValue', { value: fmtVal(r.compareValue) })}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
